import { useState, useEffect, useCallback, useRef } from 'react';
import { FilterState, GenericOption } from '../../FilterDrawer/types';
import { DEFAULT_FILTERS } from '../../FilterDrawer/constants';
import { useDashboardFilterPersistence } from 'hooks/useDashboardFilterPersistence';
import { fetchE2EFilterDetails } from 'service/service';

export interface E2EFilterOptions {
  sector: string[];
  miscellaneousSectors: string[];
  dashboardName: string[];
  frequency: string[];
  [key: string]: string[];
}

export interface E2EFilterFieldConfig {
  label: string;
  placeholder?: string;
  options: GenericOption[];
  value: GenericOption[];
  field: keyof FilterState;
  disabled?: boolean;
  loading?: boolean;
  isSingleSelect?: boolean;
  initialOptionLimit?: number;
}

const DEFAULT_FILTER_OPTIONS: E2EFilterOptions = {
  sector: [],
  miscellaneousSectors: [],
  dashboardName: [],
  frequency: [],
};

const toOptions = (values: string[] = []): GenericOption[] =>
  values.map((v) => ({ label: v, value: v }));

const isMiscellaneousSelected = (items: GenericOption[] = []) =>
  items.some((item) => {
    const value = item?.value ?? item;
    return String(value).trim().toLowerCase() === 'miscellaneous';
  });

const sessionKey = (dashboardId: string, suffix: string) =>
  `E2EFilters:${dashboardId}:${suffix}`;

export interface UseE2EFiltersConfig {
  dashboardId: string;
  dashboardName: string;
  dashboardType: 'sector' | 'program';
  extraFields?: (keyof FilterState)[];
  loadExtraFieldOptions?: (
    filters: FilterState,
    field: keyof FilterState | undefined,
    setFilterOptions: React.Dispatch<React.SetStateAction<E2EFilterOptions>>,
    setLoadingFilterFields: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  ) => Promise<void> | void;
  enablePersistence?: boolean;
}

export const useE2EDashboard = ({
  dashboardId,
  dashboardName,
  dashboardType,
  extraFields = [],
  loadExtraFieldOptions,
  enablePersistence = true,
}: UseE2EFiltersConfig) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isInitialFilterLoading, setIsInitialFilterLoading] = useState(true);
  const [loadingFilterFields, setLoadingFilterFields] = useState<Record<string, boolean>>({});
  const [filterOptions, setFilterOptions] = useState<E2EFilterOptions>(DEFAULT_FILTER_OPTIONS);

  const [appliedFilters, setAppliedFilters] = useState<FilterState>(() => {
    try {
      const cached = sessionStorage.getItem(sessionKey(dashboardId, 'appliedFilters'));
      if (cached) return JSON.parse(cached);
    } catch {
    }
    return { ...DEFAULT_FILTERS };
  });

  const latestFiltersRef = useRef<FilterState>(appliedFilters);
  useEffect(() => {
    latestFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const persistAppliedFilters = useCallback(
      (filters: FilterState) => {
        try {
          sessionStorage.setItem(sessionKey(dashboardId, 'appliedFilters'), JSON.stringify(filters));
        } catch {
          // sessionStorage unavailable/full — non-fatal, filters still work for this render
        }
      },
      [dashboardId]
  );

  const handleSavedFiltersLoaded = useCallback(
    (savedFilters: FilterState) => {
      const cached = sessionStorage.getItem(sessionKey(dashboardId, 'appliedFilters'));
      // Only load saved filters if no cache and savedFilters has actual selections
      if (!cached && savedFilters) {
        const hasSelections = Object.entries(savedFilters).some(([key, value]) => {
          if (key === 'additionalFilters') return false;
          return Array.isArray(value) && value.length > 0;
        });
        if (hasSelections) {
          setAppliedFilters(savedFilters);
          persistAppliedFilters(savedFilters);
        }
      }
    },
    [dashboardId, persistAppliedFilters]
  );

  const {
    isLoading: isFiltersLoading,
    isSaving: isSavingFilters,
    hasSavedFilters,
    savedFilters: savedDefaultFilters,
    manualSave: manualSaveFilters,
    resetToSaved: resetToSavedFilters,
  } = useDashboardFilterPersistence({
    config: { dashboardId, dashboardName, pageName: dashboardName },
    currentFilters: appliedFilters,
    onFiltersLoaded: handleSavedFiltersLoaded,
    debounceDelay: 1000,
    enabled: enablePersistence,
  });

  const loadAllDropDowns = useCallback(
    async (
      isInitial: boolean = false,
      filters: FilterState = latestFiltersRef.current,
      field?: keyof FilterState
    ) => {
      if (field) {
        setLoadingFilterFields((prev) => ({ ...prev, [field]: true }));
      }
      try {
        const globalFilters: Record<string, any> = {};
        (Object.keys(filters) as (keyof FilterState)[]).forEach((key) => {
          if (key === 'additionalFilters') return;
          const value = filters[key];
          if (Array.isArray(value) && value.length) {
            const values = (value as GenericOption[]).map((v) => v.value);
            const hasAll = values.some((v) => String(v).toLowerCase() === 'all');
            globalFilters[key] = hasAll ? ['all'] : values;
          }
        });

        const result = await fetchE2EFilterDetails({ dashboardType, globalFilters, field, extraFields });
        const apiData = result?.data?.data || {};

        setFilterOptions((prev) => ({ ...prev, ...apiData }));
      } catch (error) {
        console.error('Error loading End to End observability dashboard filter options:', error);
      } finally {
        if (field) {
          setLoadingFilterFields((prev) => ({ ...prev, [field]: false }));
        }
      }

      if (loadExtraFieldOptions && (!field || extraFields.includes(field))) {
        await loadExtraFieldOptions(filters, field, setFilterOptions, setLoadingFilterFields);
      }
    },
    [dashboardType, extraFields, loadExtraFieldOptions]
  );

  useEffect(() => {
    (async () => {
      setIsInitialFilterLoading(true);
      await loadAllDropDowns(true, latestFiltersRef.current);
      setIsInitialFilterLoading(false);
    })();
  }, []);

  const openFilterDrawer = useCallback(() => setIsFilterDrawerOpen(true), []);
  const closeFilterDrawer = useCallback(() => setIsFilterDrawerOpen(false), []);

  const handleApplyFilters = useCallback((filters: FilterState) => {
    setAppliedFilters(filters);
    persistAppliedFilters(filters);
  }, [persistAppliedFilters]);

  const handleSaveAsDefault = useCallback(
    async (filters: FilterState) => {
      setAppliedFilters(filters);
      persistAppliedFilters(filters);
      return manualSaveFilters(filters);
    },
    [manualSaveFilters, persistAppliedFilters]
  );

  const handleResetToDefault = useCallback(async () => {
    const result = await resetToSavedFilters();
    if (result?.success && result.filters) {
      setAppliedFilters(result.filters);
      persistAppliedFilters(result.filters);
      await loadAllDropDowns(true, result.filters);
    }
    return result;
  }, [resetToSavedFilters, loadAllDropDowns, persistAppliedFilters]);

  return {
    isFilterDrawerOpen,
    openFilterDrawer,
    closeFilterDrawer,
    appliedFilters,
    filterOptions,
    setFilterOptions,
    loadingFilterFields,
    setLoadingFilterFields,
    isInitialFilterLoading,
    loadAllDropDowns,
    handleApplyFilters,
    handleSaveAsDefault,
    handleResetToDefault,
    hasSavedFilters,
    savedDefaultFilters,
    isSavingFilters,
    isFiltersLoading,
  };
};

export const buildBaseDashboardFilterFields = (
  filterOptions: E2EFilterOptions,
  appliedFilters: FilterState,
  loadingFilterFields: Record<string, boolean>
): E2EFilterFieldConfig[] => {
  const showMiscellaneousSectors = isMiscellaneousSelected(appliedFilters.sector);

  return [
    {
      label: 'Sectors',
      placeholder: 'Select sectors...',
      options: toOptions(filterOptions.sector),
      value: appliedFilters.sector || [],
      field: 'sector',
      loading: loadingFilterFields.sector,
    },
    ...(showMiscellaneousSectors
      ? [
          {
            label: 'Miscellaneous Sectors',
            placeholder: filterOptions.miscellaneousSectors.length ? 'Select misc sectors...' : 'None',
            options: toOptions(filterOptions.miscellaneousSectors),
            value: appliedFilters.miscellaneousSectors || [],
            field: 'miscellaneousSectors' as keyof FilterState,
            loading: loadingFilterFields.miscellaneousSectors,
          },
        ]
      : []),
    {
      label: 'Dashboard Name',
      placeholder: 'Select dashboards...',
      options: toOptions(filterOptions.dashboardName),
      value: appliedFilters.dashboardName || [],
      field: 'dashboardName',
      loading: loadingFilterFields.dashboardName,
    },
    {
      label: 'Frequency',
      placeholder: 'Select frequencies...',
      options: toOptions(filterOptions.frequency),
      value: appliedFilters.frequency || [],
      field: 'frequency',
      loading: loadingFilterFields.frequency,
    },
  ];
};

interface ProgramExtraFieldSpec {
  field: keyof FilterState;
  label: string;
  placeholder: string;
}

const PROGRAM_EXTRA_FIELDS: Record<string, ProgramExtraFieldSpec[]> = {
  IBP: [
    { field: 'gdo', label: 'GDO', placeholder: 'Select GDO...' },
    { field: 'market', label: 'Market', placeholder: 'Select markets...' },
  ],
  MOSAIC: [
    { field: 'businessStream', label: 'Business Stream', placeholder: 'Select business streams...' },
    { field: 'market', label: 'Market', placeholder: 'Select markets...' },
  ],
  EDF: [
    { field: 'domain', label: 'Domain', placeholder: 'Select domains...' },
  ],
  MDIP: [
    { field: 'businessApplication', label: 'Business Application', placeholder: 'Select business applications...' },
  ],
};

const getMatchingProgramKey = (programValue: string): string | null => {
  const normalizedValue = String(programValue || '').trim().toUpperCase();
  if (!normalizedValue) return null;

  for (const key of Object.keys(PROGRAM_EXTRA_FIELDS)) {
    if (normalizedValue.includes(key)) {
      return key;
    }
  }

  return null;
};

export const PROGRAM_DASHBOARD_EXTRA_FIELD_KEYS: (keyof FilterState)[] = [
  'program',
  'gdo',
  'market',
  'businessStream',
  'domain',
  'businessApplication',
];

export const buildProgramDashboardFilterFields = (
  filterOptions: E2EFilterOptions,
  appliedFilters: FilterState,
  loadingFilterFields: Record<string, boolean>
): E2EFilterFieldConfig[] => {
  const base = buildBaseDashboardFilterFields(filterOptions, appliedFilters, loadingFilterFields);
 
  const sectorFields = base.filter((f) => f.field === 'sector' || f.field === 'miscellaneousSectors');
  const remainingBaseFields = base.filter((f) => f.field !== 'sector' && f.field !== 'miscellaneousSectors');
 
  const programField: E2EFilterFieldConfig = {
    label: 'Program',
    placeholder: 'Select programs...',
    options: toOptions(filterOptions.program || []),
    value: appliedFilters.program || [],
    field: 'program',
    loading: loadingFilterFields.program,
    initialOptionLimit: 100,
  };
 
  const selectedProgramValues = (appliedFilters.program || [])
    .map((program) => String(program?.value || '').trim())
    .filter(Boolean);

  const matchedProgramKeys = selectedProgramValues.reduce<string[]>((keys, programValue) => {
    const matchingKey = getMatchingProgramKey(programValue);
    if (matchingKey && !keys.includes(matchingKey)) {
      keys.push(matchingKey);
    }
    return keys;
  }, []);

  const extraFieldConfigs: E2EFilterFieldConfig[] = matchedProgramKeys.flatMap((programKey) => {
    const specs = PROGRAM_EXTRA_FIELDS[programKey] || [];
    return specs.map(({ field, label, placeholder }) => ({
      label,
      placeholder,
      options: toOptions(filterOptions[field] || []),
      value: (appliedFilters[field] as GenericOption[]) || [],
      field,
      loading: loadingFilterFields[field],
      isIndented: true,
      indentLevel: 1,
    }));
  });
 
  const seen = new Set<string>();
  const dedupedExtraFields = extraFieldConfigs.filter((f) => {
    if (seen.has(f.field as string)) return false;
    seen.add(f.field as string);
    return true;
  });
 
  return [...sectorFields, programField, ...remainingBaseFields, ...dedupedExtraFields];
};